/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useState, useContext, useMemo, useEffect } from 'react';
import { addMonthsUTC, firstOfMonth, isStrEmptyOrNull } from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';
import { useBrwrContext } from './BorrowerContext';
import useLoanServicing from '../common/hooks/useLoanServicing';
import { BatchEndpoint, HandleChangeType, Status, useBatchEditable } from '../common/hooks/useBatchEditable';
import { LOAN_FORM_EVENT_DATE_CODES, LOAN_FORM_RELATIONSHIP_CODES } from '../contracts/constants';
import useBorrowersInOrder from '../common/hooks/useBorrowersInOrder';
import { useLDCContext } from './LoanDefaultCodesContext';
import { enqueueSnackbar } from 'notistack';
import useQidList from '../common/hooks/useQidList';

type StatusAlert = {
  status: string;
  message: string;
};

type LoanInformationContextType = {
  servicingData: ServicingData;
  refetchServicingData: () => void;
  handleServicingChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handlePropChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLnptChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLoanChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLastYearLnptPayChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLnptPayChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLossMitiChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLoanEventChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLoanContactChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleLoanBorrowersChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  handleAnnualFeeChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  propData: PropData;
  lnptData: LnptData;
  loanData: LoanData;
  lastYearLnptPayData: LastYearLnptPayData;
  lnptPayData: LnptPayData;
  lastPaymentData: LastPaymentData;
  annualFeeData: AnnualFeeData;
  paymentHistoryData: PaymentHistoryData;
  lossMitData: LossMitData;
  loanEvents: BatchEventResp;
  loanContacts: BatchContactResp;
  drawEndSeconds: number;
  repayStartSeconds: number;
  repayEndSeconds: number;
  borrowersArr: BorrowerData[];
  loanBorrowers: BatchBorrowerResp;
  addedBorrower: NewBorrowerInput;
  handleAddBorrowerChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  statusAlert: StatusAlert;
  setStatusAlert: React.Dispatch<React.SetStateAction<StatusAlert>>;
  nameModalBorrowerNumber: string;
  setNameModalBorrowerNumber: React.Dispatch<React.SetStateAction<string>>;
  privacyModalNumber: string;
  setPrivacyModalNumber: React.Dispatch<React.SetStateAction<string>>;
  saveAllData: () => Promise<void>;
  removedStopIndices: Set<number>;
  setRemovedStopIndices: React.Dispatch<React.SetStateAction<Set<number>>>;
  addedStopCodes: Set<string>;
  setAddedStopCodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleCancel: () => void;
  hasChanges: boolean;
  loading: boolean;
  bttcanytime: BttcAnytime;
  handleAnytimeChange: (type: HandleChangeType, variable?: string, val?: any) => void;
  contactsQID: QIDData[];
  originalLoanData: LoanData;
};

const LoanInformationContext = createContext<LoanInformationContextType | undefined>(undefined);

const useLoanInformationContext = () => {
  const loanInformationContext = useContext(LoanInformationContext);
  if (loanInformationContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('loanInformationContext must be inside a LoanInformationProvider');
  }
  return loanInformationContext;
};

type loanEventCode =
  | 'SRG'
  | 'AEA'
  | 'SRM'
  | 'V04'
  | 'S7B'
  | 'S7A'
  | 'S79'
  | 'S78'
  | 'OSV'
  | 'F60'
  | 'PRI'
  | 'DFL'
  | 'ASD'
  | 'MCD'
  | 'MOE'
  | 'SRC'
  | 'SDT'
  | 'C06'
  | 'SER'
  | 'TPP'
  | 'TE1'
  | 'DTM'
  | 'FDS'
  | 'DAX'
  | 'DAK';

type loanContactCode =
  | 'FA'
  | 'MO'
  | 'FL'
  | 'ML'
  | 'SP'
  | 'SN'
  | 'DT'
  | 'FR'
  | 'CO'
  | 'AT'
  | 'FI'
  | 'FE'
  | 'HC'
  | 'LW'
  | 'TE';

type BatchEventResp = {
  [K in loanEventCode]: EventObj;
};

type BatchContactResp = {
  [K in loanContactCode]: ContactData;
};

type BatchBorrowerResp = {
  [key: string]: BorrowerData;
};

type BttcAnytime = {
  '0': { anytime: boolean; changed: boolean };
  '1': { anytime: boolean; changed: boolean };
};

const LoanInformationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentDate = new Date();
  const todaysDate = `${currentDate.getFullYear()}${(currentDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${currentDate.getDate().toString().padStart(2, '0')}`;
  const currentYear = new Date().getFullYear();
  const lastYearDecFirst = `${currentYear - 1}1201`;
  const [removedStopIndices, setRemovedStopIndices] = useState<Set<number>>(new Set());
  const [addedStopCodes, setAddedStopCodes] = useState<Set<string>>(new Set());
  const { loanNumber, refetchLoanData } = useLoanContext();
  const { reloadBorrowerData } = useBrwrContext();
  const { reloadLoanServicing } = useLoanServicing();
  const {
    curr: servicingData,
    handleChange: handleServicingChange,
    saveChangesToBackend: saveServicing,
    hasChanges: hasServicingChanges,
    status: servicingStatus,
    refetch: refetchServicingData,
  } = useBatchEditable<ServicingData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? 'live/' + process.env.REACT_APP_API_SERVICING.replace('?', loanNumber) : '',
      requestTypes: ['GET', 'POST'],
    },
  });
  const {
    curr: propData,
    handleChange: handlePropChange,
    saveChangesToBackend: saveProp,
    hasChanges: hasPropChanges,
    status: propStatus,
  } = useBatchEditable<PropData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? process.env.REACT_APP_PROPERTY.replace('?', loanNumber) + '001' : '',
      requestTypes: ['GET', 'POST'],
    },
  });
  const {
    curr: lnptData,
    handleChange: handleLnptChange,
    saveChangesToBackend: saveLnpt,
    hasChanges: hasLnptChanges,
    status: lnptStatus,
  } = useBatchEditable<LnptData>({
    endpoint: {
      path:
        !isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(todaysDate)
          ? process.env.REACT_APP_LOAN_BALANCE.replace('?', loanNumber) + todaysDate
          : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const {
    curr: loanData,
    prev: originalLoanData,
    handleChange: handleLoanChange,
    saveChangesToBackend: saveLoan,
    hasChanges: hasLoanChanges,
    status: loanStatus,
  } = useBatchEditable<LoanData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? process.env.REACT_APP_LOAN.replace('?', loanNumber) : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const {
    curr: lastYearLnptPayData,
    handleChange: handleLastYearLnptPayChange,
    saveChangesToBackend: saveLastYearLnptPay,
    hasChanges: hasLastYearLnptPayChanges,
    status: lastYearLnptPayStatus,
  } = useBatchEditable<LastYearLnptPayData>({
    endpoint: {
      path:
        !isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(lastYearDecFirst)
          ? process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanNumber) + lastYearDecFirst
          : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const {
    curr: lnptPayData,
    handleChange: handleLnptPayChange,
    saveChangesToBackend: saveLnptPay,
    hasChanges: hasLnptPayChanges,
    status: lnptPayStatus,
  } = useBatchEditable<LnptPayData>({
    endpoint: {
      path:
        !isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(firstOfMonth)
          ? process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanNumber) + firstOfMonth
          : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const {
    curr: annualFeeData,
    handleChange: handleAnnualFeeChange,
    saveChangesToBackend: saveAnnualFee,
    hasChanges: hasAnnualFeeChanges,
    status: annualFeeStatus,
  } = useBatchEditable<AnnualFeeData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? process.env.REACT_APP_ANNUAL_CHARGE.replace('?', loanNumber) : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const lastPaymentDate = useMemo(() => {
    const lpi = loanData?.lpi;
    if (!lpi) return '';
    const d = new Date(Number(lpi) * 1000);
    const Y = d.getFullYear();
    const M = String(d.getMonth() + 1).padStart(2, '0');
    const D = String(d.getDate()).padStart(2, '0');
    return `${Y}${M}${D}`;
  }, [loanData?.lpi]);

  const { curr: lastPaymentData, status: lastPaymentStatus } = useBatchEditable<LastPaymentData>({
    endpoint: {
      path:
        !isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(lastPaymentDate)
          ? process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanNumber) + lastPaymentDate
          : '',
      requestTypes: ['GET'],
    },
  });

  const { curr: paymentHistoryData, status: paymentHistoryStatus } = useBatchEditable<PaymentHistoryData>({
    endpoint: {
      path:
        !isStrEmptyOrNull(loanNumber) && !isStrEmptyOrNull(todaysDate)
          ? process.env.REACT_APP_PAY_HISTORY.replace('?', loanNumber) + todaysDate
          : '',
      requestTypes: ['GET'],
    },
  });

  const {
    curr: lossMitData,
    handleChange: handleLossMitiChange,
    saveChangesToBackend: saveLossMiti,
    hasChanges: hasLossMitiChanges,
    status: lossMitiStatus,
  } = useBatchEditable<LossMitData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? process.env.REACT_APP_LOSS_MITI.replace('?', loanNumber) : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const getBatchEventsObj = useCallback(() => {
    if (!loanNumber) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    LOAN_FORM_EVENT_DATE_CODES.forEach((eventcode) => {
      batchEndpoints.push({
        endpointName: eventcode,
        path: process.env.REACT_APP_EVENT_DATES.replace('?', loanNumber) + eventcode,
        requestTypes: ['GET', 'POST'],
      });
    });
    return batchEndpoints;
  }, [loanNumber]);

  const {
    curr: loanEvents,
    handleChange: handleLoanEventChange,
    saveChangesToBackend: saveLoanEvents,
    hasChanges: hasLoanEventsChanges,
    status: loanEventsStatus,
  } = useBatchEditable<BatchEventResp>({
    batchEndpoints: getBatchEventsObj(),
  });

  const { qid: contactsQID, loadingQid: loadingContactsQid } = useQidList('TABE_CD.003');

  const getBatchContactsObj = useCallback(() => {
    if (!loanNumber || loadingContactsQid || contactsQID.length < 1) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    contactsQID.forEach((contactcode) => {
      batchEndpoints.push({
        endpointName: contactcode.code,
        path: process.env.REACT_APP_CONTACT.replace('?', loanNumber) + contactcode.code,
        requestTypes: ['GET', 'POST'],
      });
    });
    return batchEndpoints;
  }, [loanNumber, loadingContactsQid, contactsQID]);

  const {
    curr: loanContacts,
    handleChange: handleLoanContactChange,
    saveChangesToBackend: saveLoanContacts,
    hasChanges: hasLoanContactsChanges,
    status: loanContactsStatus,
  } = useBatchEditable<BatchContactResp>({
    batchEndpoints: getBatchContactsObj(),
  });

  const { borrowersToRender, refetchBorrowersInOrder, loadingBorrowersInOrder } = useBorrowersInOrder();

  const getBatchBorrowersObj = useCallback(() => {
    if (borrowersToRender.length == 0 || loadingBorrowersInOrder) return null;
    if (!loanNumber) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    borrowersToRender.forEach((brwrNo) => {
      batchEndpoints.push({
        endpointName: brwrNo,
        path: process.env.REACT_APP_BRWR.replace('live/', '').replace('?', loanNumber) + brwrNo,
        requestTypes: ['GET', 'POST'],
      });
    });
    return batchEndpoints;
  }, [loanNumber, loadingBorrowersInOrder, borrowersToRender]);

  const {
    curr: loanBorrowers,
    handleChange: handleLoanBorrowersChange,
    saveChangesToBackend: saveLoanBorrowers,
    hasChanges: hasLoanBorrowersChanges,
    status: loanBorrowersStatus,
  } = useBatchEditable<BatchBorrowerResp>({
    batchEndpoints: getBatchBorrowersObj(),
  });

  const getBorrowers = useCallback(() => {
    return borrowersToRender
      .map((borrowerNumber) => loanBorrowers[borrowerNumber])
      .filter((borrower): borrower is BorrowerData => borrower !== undefined);
  }, [loanBorrowers, borrowersToRender]);

  const borrowersArr = getBorrowers();

  const drawEndSeconds = useMemo<number | null>(() => {
    const sdtSeconds = loanEvents?.SDT?.activeDate;
    const monthsOfInterestOnly = Number(loanData?.interestOnlyPeriod);

    if (!Number.isFinite(sdtSeconds) || !Number.isFinite(monthsOfInterestOnly)) return null;

    return addMonthsUTC(sdtSeconds, monthsOfInterestOnly);
  }, [loanEvents?.SDT?.activeDate, loanData?.interestOnlyPeriod]);

  const repayStartSeconds = useMemo<number | null>(() => {
    return drawEndSeconds == null ? null : drawEndSeconds + 86_400;
  }, [drawEndSeconds]);

  const repayEndSeconds = useMemo<number | null>(() => {
    if (repayStartSeconds == null) return null;

    const totalTermMonths = Number(loanData?.loanTerm);
    const ioMonths = Number(loanData?.interestOnlyPeriod);
    if (!Number.isFinite(totalTermMonths) || !Number.isFinite(ioMonths)) return null;

    const repayMonths = Math.max(0, totalTermMonths - ioMonths);

    return addMonthsUTC(repayStartSeconds, repayMonths) - 86_400;
  }, [repayStartSeconds, loanData?.loanTerm, loanData?.interestOnlyPeriod]);

  const {
    curr: addedBorrower,
    handleChange: handleAddBorrowerChange,
    saveChangesToBackend: saveAddedBorrower,
    hasChanges: hasAddedBorrowerChanges,
    status: addedBorrowerStatus,
  } = useBatchEditable<NewBorrowerInput>({
    endpoint: {
      path: loanNumber ? `live/loan/${loanNumber}/borrower/add` : '',
      requestTypes: ['POST'],
    },
  });

  const [nameModalBorrowerNumber, setNameModalBorrowerNumber] = useState<string | undefined>(undefined);
  const [privacyModalNumber, setPrivacyModalNumber] = useState('');
  const [statusAlert, setStatusAlert] = useState({ status: '', message: '' });

  const { LDCStatus, LDCFunctions } = useLDCContext();

  const { hasChanges: hasLDCChanges, loading: loadingLDC } = LDCStatus;
  const { updateLDCInfo, handleEdit, cancelLDCChanges } = LDCFunctions;

  useEffect(() => {
    handleEdit();
  }, []);

  const { curr: bttcanytime, handleChange: handleAnytimeChange } = useBatchEditable<BttcAnytime>({
    initialObj: { '0': { anytime: false, changed: false }, '1': { anytime: false, changed: false } },
  });

  const saveAllData = useCallback(async () => {
    const promises: Promise<void>[] = [];
    if (hasLDCChanges) {
      promises.push(updateLDCInfo());
    }

    promises.push(saveAddedBorrower());
    promises.push(saveLastYearLnptPay());
    promises.push(saveLnpt());
    promises.push(saveLoan());
    promises.push(saveLoanBorrowers());
    promises.push(saveLoanContacts());
    promises.push(saveLoanEvents());
    promises.push(saveLossMiti());
    promises.push(saveProp());
    promises.push(saveServicing());
    promises.push(saveLnptPay());
    promises.push(saveAnnualFee());
    await Promise.all(promises).then(() => {
      setRemovedStopIndices(new Set());
      setAddedStopCodes(new Set());

      refetchBorrowersInOrder();
      refetchLoanData();
      reloadBorrowerData();
      reloadLoanServicing();
    });
  }, [
    hasLDCChanges,
    updateLDCInfo,
    saveAddedBorrower,
    saveLastYearLnptPay,
    saveLnpt,
    saveLoan,
    saveLoanBorrowers,
    saveLoanContacts,
    saveLoanEvents,
    saveLossMiti,
    saveProp,
    saveServicing,
    saveLnptPay,
    saveAnnualFee,
    refetchBorrowersInOrder,
    refetchLoanData,
    reloadBorrowerData,
    reloadLoanServicing,
  ]);

  const handleCancel = useCallback(() => {
    try {
      setRemovedStopIndices(new Set());
      setAddedStopCodes(new Set());
      cancelLDCChanges();
      handleAddBorrowerChange('cancel');
      handleLastYearLnptPayChange('cancel');
      handleLnptChange('cancel');
      handleLnptPayChange('cancel');
      handleLoanBorrowersChange('cancel');
      handleLoanChange('cancel');
      handleLoanContactChange('cancel');
      handleLoanEventChange('cancel');
      handleLossMitiChange('cancel');
      handlePropChange('cancel');
      handleServicingChange('cancel');
      handleAnytimeChange('cancel');
      handleAnnualFeeChange('cancel');
      enqueueSnackbar('Changes discarded. No data was saved.', { variant: 'info' });
    } catch (error) {
      console.error('Error resetting data:', error);
      enqueueSnackbar('Error discarding changes. Please try again.', { variant: 'error' });
    }
  }, [
    cancelLDCChanges,
    enqueueSnackbar,
    handleAddBorrowerChange,
    handleLastYearLnptPayChange,
    handleLnptChange,
    handleLnptPayChange,
    handleLoanBorrowersChange,
    handleLoanChange,
    handleLoanContactChange,
    handleLoanEventChange,
    handleLossMitiChange,
    handlePropChange,
    handleServicingChange,
    handleAnytimeChange,
    handleAnnualFeeChange,
  ]);

  const hasChanges =
    hasAddedBorrowerChanges ||
    hasLDCChanges ||
    hasLastYearLnptPayChanges ||
    hasLnptChanges ||
    hasLnptPayChanges ||
    hasLoanBorrowersChanges ||
    hasLoanChanges ||
    hasLoanContactsChanges ||
    hasLoanEventsChanges ||
    hasLossMitiChanges ||
    hasPropChanges ||
    hasServicingChanges ||
    hasAnnualFeeChanges;

  const statuses = [
    servicingStatus,
    propStatus,
    lnptStatus,
    servicingStatus,
    loanStatus,
    lastYearLnptPayStatus,
    lnptPayStatus,
    annualFeeStatus,
    lastPaymentStatus,
    paymentHistoryStatus,
    lossMitiStatus,
    loanEventsStatus,
    loanContactsStatus,
    loanBorrowersStatus,
    addedBorrowerStatus,
  ];

  const loading = statuses.includes(Status.LOADING) || loadingLDC || loadingBorrowersInOrder;

  const contextValue = useMemo(
    () => ({
      servicingData,
      refetchServicingData,
      handleServicingChange,
      propData,
      handlePropChange,
      lnptData,
      handleLnptChange,
      loanData,
      handleLoanChange,
      lastYearLnptPayData,
      lnptPayData,
      handleLastYearLnptPayChange,
      handleLnptPayChange,
      lastPaymentData,
      annualFeeData,
      handleAnnualFeeChange,
      paymentHistoryData,
      lossMitData,
      handleLossMitiChange,
      loanContacts,
      loanEvents,
      drawEndSeconds,
      repayStartSeconds,
      repayEndSeconds,
      loanBorrowers,
      handleLoanEventChange,
      handleLoanContactChange,
      handleLoanBorrowersChange,
      addedBorrower,
      handleAddBorrowerChange,
      borrowersArr,
      statusAlert,
      setStatusAlert,
      nameModalBorrowerNumber,
      setNameModalBorrowerNumber,
      privacyModalNumber,
      setPrivacyModalNumber,
      saveAllData,
      removedStopIndices,
      setRemovedStopIndices,
      addedStopCodes,
      setAddedStopCodes,
      handleCancel,
      hasChanges,
      loading,
      bttcanytime,
      handleAnytimeChange,
      contactsQID,
      originalLoanData,
    }),
    [
      servicingData,
      refetchServicingData,
      handleServicingChange,
      propData,
      handlePropChange,
      lnptData,
      handleLnptChange,
      loanData,
      handleLoanChange,
      lastYearLnptPayData,
      lnptPayData,
      handleLastYearLnptPayChange,
      handleLnptPayChange,
      lastPaymentData,
      annualFeeData,
      handleAnnualFeeChange,
      paymentHistoryData,
      lossMitData,
      handleLossMitiChange,
      loanContacts,
      loanEvents,
      drawEndSeconds,
      repayStartSeconds,
      repayEndSeconds,
      loanBorrowers,
      handleLoanEventChange,
      handleLoanContactChange,
      handleLoanBorrowersChange,
      addedBorrower,
      handleAddBorrowerChange,
      borrowersArr,
      statusAlert,
      setStatusAlert,
      nameModalBorrowerNumber,
      setNameModalBorrowerNumber,
      privacyModalNumber,
      setPrivacyModalNumber,
      saveAllData,
      removedStopIndices,
      setRemovedStopIndices,
      addedStopCodes,
      setAddedStopCodes,
      handleCancel,
      hasChanges,
      loading,
      bttcanytime,
      handleAnytimeChange,
      contactsQID,
      originalLoanData,
    ],
  );

  return <LoanInformationContext.Provider value={contextValue}>{children}</LoanInformationContext.Provider>;
};

export { LoanInformationProvider, useLoanInformationContext };
